import { Flex, Icon, useColorMode } from "@chakra-ui/react";
import React from "react";

// Hauteur du header fixe de DocShell (h={20} → 80px), plus une respiration
const HEADER_OFFSET = 96;

const DocNavItem = (props) => {
  const { icon, children, to, ...rest } = props;
  const [activeId, setActiveId] = React.useState();
  const { colorMode } = useColorMode();
  const itemRef = React.useRef(null);

  // Section active : la dernière dont le titre est passé au-dessus de la ligne
  // de déclenchement. En bas du conteneur, c'est toujours la dernière section
  // qui est active — sinon la ou les dernières entrées, trop courtes pour
  // atteindre cette ligne, ne seraient jamais surlignées.
  React.useEffect(() => {
    const container = document.querySelector("main");
    if (!container) return;

    let frame = null;

    const update = () => {
      frame = null;
      const headings = Array.from(container.querySelectorAll("h1[id]"));
      if (!headings.length) return;

      const scrollTop = container.scrollTop;
      const maxScroll = Math.max(
        0,
        container.scrollHeight - container.clientHeight
      );
      const line = container.getBoundingClientRect().top + HEADER_OFFSET;

      // Position de défilement à laquelle chaque titre atteint la ligne de
      // déclenchement. La valeur ne dépend pas du défilement courant.
      const trigger = headings.map(
        (heading) => scrollTop + heading.getBoundingClientRect().top - line
      );

      // Les dernières sections sont souvent trop courtes pour que leur titre
      // puisse atteindre cette ligne : leur seuil dépasse la course
      // disponible et elles ne seraient jamais actives. On répartit alors la
      // course restante entre elles, plutôt que de les faire toutes basculer
      // au tout dernier pixel.
      const firstUnreachable = trigger.findIndex((value) => value > maxScroll);
      if (firstUnreachable !== -1) {
        const start = firstUnreachable === 0 ? 0 : trigger[firstUnreachable - 1];
        const span = Math.max(0, maxScroll - start);
        const count = headings.length - firstUnreachable;
        for (let k = 0; k < count; k += 1) {
          trigger[firstUnreachable + k] = start + (span * (k + 1)) / (count + 1);
        }
      }

      let current = headings[0].id;
      for (let i = 0; i < headings.length; i += 1) {
        if (scrollTop >= trigger[i] - 1) current = headings[i].id;
      }

      // Garde-fou : tout en bas, c'est la dernière section.
      if (scrollTop + container.clientHeight >= container.scrollHeight - 4) {
        current = headings[headings.length - 1].id;
      }

      setActiveId(current);
    };

    const onScroll = () => {
      if (frame === null) frame = requestAnimationFrame(update);
    };

    update();
    container.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onScroll);
    return () => {
      if (frame !== null) cancelAnimationFrame(frame);
      container.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onScroll);
    };
  }, []);
  // Le sommaire déborde de la hauteur d'écran dès qu'il y a beaucoup de
  // sections : on l'amène sur l'entrée active, sinon le surlignage reste
  // invisible pendant la lecture.
  React.useEffect(() => {
    if (activeId !== to) return;
    const item = itemRef.current;
    const list = item?.parentElement;
    if (!item || !list || list.scrollHeight <= list.clientHeight) return;

    const itemBox = item.getBoundingClientRect();
    const listBox = list.getBoundingClientRect();

    if (itemBox.top < listBox.top + 8) {
      list.scrollTo({
        top: list.scrollTop + itemBox.top - listBox.top - 8,
        behavior: "smooth",
      });
    } else if (itemBox.bottom > listBox.bottom - 8) {
      list.scrollTo({
        top: list.scrollTop + itemBox.bottom - listBox.bottom + 8,
        behavior: "smooth",
      });
    }
  }, [activeId, to]);

  return (
    <Flex
      ref={itemRef}
      align="center"
      onClick={(e) => {
        e.preventDefault();
        // getElementById et non querySelector : les ancres sont numériques
        // ("1", "2", …) et "#1" n'est pas un sélecteur CSS valide.
        const target = document.getElementById(to);
        if (!target) return;

        // Le contenu défile dans <main>, pas dans la fenêtre : on décale de la
        // hauteur du header fixe pour que le titre ne passe pas dessous.
        const container = target.closest("main");
        if (container && container.scrollHeight > container.clientHeight) {
          const delta =
            target.getBoundingClientRect().top -
            container.getBoundingClientRect().top;
          container.scrollTo({
            top: container.scrollTop + delta - HEADER_OFFSET,
            behavior: "smooth",
          });
        } else {
          target.scrollIntoView({ behavior: "smooth", block: "start" });
        }
      }}
      px="4"
      mx="2"
      rounded="md"
      py="3"
      bg={activeId === to ? "brand.500" : null}
      cursor="pointer"
      color={
        activeId === to
          ? "whiteAlpha.900"
          : colorMode === "light"
          ? "blackAlpha.700"
          : "white"
      }
      _hover={{
        bg: "brand.500",
        color: "whiteAlpha.900",
      }}
      role="group"
      fontSize={{ base: "sm", "2xl": "md", "3xl": "lg", "5xl": "xl" }}
      fontWeight="bold"
      transition=".15s ease"
      {...rest}
    >
      {icon && (
        <Icon
          mr="2"
          boxSize="4"
          _groupHover={{
            color: "gray.300",
          }}
          as={icon}
        />
      )}
      {children}
    </Flex>
  );
};

export default DocNavItem;
